import { useMemo, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import {
  useRtgsBankTemplates,
  useDeleteRtgsBankTemplate,
} from "hooks/useRtgsBankTemplate"
import { previewRtgsBankTemplate } from "api/rtgsBankTemplateApi"

import RtgsBankTemplateAddEdit from "./RtgsBankTemplateAddEdit"

const RtgsBankTemplateList = () => {
  document.title = "RTGS Bank Templates"

  const navigate = useNavigate()
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [previewLoadingId, setPreviewLoadingId] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useRtgsBankTemplates(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteRtgsBankTemplate()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) {
      return
    }
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Bank template deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      // axios interceptor handles error toast
    }
  }

  const handlePreview = async item => {
    if (!item.template_path) {
      toast.warning("No template file uploaded. Upload a pad template first to preview the overlay.")
      return
    }
    await previewRtgsBankTemplate(item.id, {
      onStart: () => setPreviewLoadingId(item.id),
      onEnd: () => setPreviewLoadingId(null),
    })
  }

  const columns = useMemo(
    () => [
      {
        header: "Bank Template",
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="fw-bold text-primary mb-1">
                {item.bank_name ?? "-"}
              </div>
              <small className="text-muted d-block">ID: {item.id}</small>
              <div className="d-sm-none mt-1 d-flex flex-wrap gap-1">
                {!item.template_path ? (
                  <Badge color="secondary">Default</Badge>
                ) : item.template_file_missing ? (
                  <Badge color="danger">Missing</Badge>
                ) : (
                  <Badge color="success">Uploaded</Badge>
                )}
                <Badge color={item.is_active ? "success" : "secondary"}>
                  {item.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Template File",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) =>
          !row.original.template_path ? (
            <Badge color="secondary">Default</Badge>
          ) : row.original.template_file_missing ? (
            <Badge color="danger">Missing</Badge>
          ) : (
            <Badge color="success">Uploaded</Badge>
          ),
      },
      {
        header: "Status",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <Badge color={row.original.is_active ? "success" : "secondary"}>
            {row.original.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => (
          <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
            <li>
              <Button
                color="secondary"
                size="sm"
                onClick={() => handlePreview(row.original)}
                disabled={previewLoadingId === row.original.id}
                id={`preview-rtgs-template-${row.original.id}`}
              >
                {previewLoadingId === row.original.id ? (
                  <i className="mdi mdi-loading mdi-spin" />
                ) : (
                  <i className="mdi mdi-eye-outline" />
                )}
              </Button>
              <UncontrolledTooltip
                placement="top"
                target={`preview-rtgs-template-${row.original.id}`}
              >
                Preview with dummy data
              </UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.RTGS_BANK_TEMPLATE.EDIT]}>
              <li>
                <Button
                  color="primary"
                  size="sm"
                  disabled={
                    !row.original.template_path ||
                    row.original.template_file_missing
                  }
                  onClick={() =>
                    navigate(`/rtgs-bank-template/${row.original.id}/configure`)
                  }
                  id={`configure-rtgs-template-${row.original.id}`}
                >
                  <i className="mdi mdi-target" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`configure-rtgs-template-${row.original.id}`}
                >
                  {!row.original.template_path
                    ? "Upload a pad template first"
                    : row.original.template_file_missing
                    ? "Template file missing on this server, re-upload it"
                    : "Configure field positions"}
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.RTGS_BANK_TEMPLATE.EDIT]}>
              <li>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEditClick(row.original)}
                  id={`edit-rtgs-template-${row.original.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-rtgs-template-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.RTGS_BANK_TEMPLATE.DELETE]}>
              <li>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(row.original)}
                  id={`delete-rtgs-template-${row.original.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`delete-rtgs-template-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    [navigate, previewLoadingId]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="RTGS Bank Templates" breadcrumbItem="Bank Template List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total{" "}
            <span className="badge badge-soft-info">
              {paginationInfo?.total ?? 0}
            </span>
          </h5>
        </Col>
        <Col
          xs={6}
          className="d-flex justify-content-end align-items-center gap-1"
        >
          <Button color="light" onClick={refetch}>
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          <CanView access={[ACCESS.RTGS_BANK_TEMPLATE.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>
              <i className="bx bx-plus" />
              <span className="d-none d-sm-inline ms-1">Add New</span>
            </Button>
          </CanView>
          {hasActiveFilters && (
            <Button color="danger" onClick={resetParams}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={queryParams.per_page}
      />

      <RtgsBankTemplateAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default RtgsBankTemplateList
